import { useContext, useEffect } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import { FormattedMessage, useIntl } from 'react-intl'
import Sheet from 'components/utils/Sheet'
import Group from 'components/utils/Group'
import Label from 'LuxKit/Label/Label'
import Heading from 'LuxKit/Typography/Heading'
import BodyText from 'LuxKit/Typography/BodyText'
import TextButton from 'LuxKit/Button/TextButton'
import ProductPaletteProvider from 'contexts/ProductPaletteContext'
import FormatCurrency from 'components/Common/FormatCurrency'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { useScreenSizeOnly } from 'hooks/useScreenSize'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { getBaseTierOfferWithJoiningFee } from 'selectors/luxPlusSelectors'
import { fetchProductSubscription } from 'actions/LuxPlusActions'
import useLuxPlusLandingActions from 'luxPlus/hooks/useLuxPlusLandingActions'
import getRegionSaveAmountCopy from 'luxPlus/lib/getRegionSaveAmountCopy'
import GeoContext from 'contexts/geoContext'

const BannerContainer = styled(Sheet)`
  position: relative;
  width: calc(100% - ${rem(32)});
  max-width: ${rem(620)};
  margin-inline: auto;
  border: ${rem(1)} solid ${props => props.theme.palette.messaging.urgency.normalForeground};

  ${mediaQueryUp.desktop} {
    width: 100%;
    margin-inline: 0;
  }
`

// On mobile and tablet the label sits inline, centered, at the top of the stack. From desktop up
// it floats absolutely, overlapping the container's top border, matching the desktop Figma design.
const LabelPosition = styled.div`
  display: flex;
  justify-content: center;
  width: 100%;
  margin-block-end: ${rem(16)};

  ${mediaQueryUp.desktop} {
    position: absolute;
    top: ${rem(-11)};
    inset-inline-start: ${rem(-1)};
    width: auto;
    margin-block-end: 0;
  }
`

const PriceText = styled(Heading)`
  text-decoration: line-through;
`

const RegularWeightText = styled.span`
  font-weight: ${props => props.theme.font.primary.weight.regular};
`

const FootnoteMark = styled.sup`
  font-size: 0.55em;
`

const NoWrapLabel = styled(Label)`
  white-space: nowrap;
  flex-shrink: 0;
`

// Both buttons split the row evenly on mobile (matching the Figma mobile frame); from tablet up
// they size to their own content instead, sitting inline together.
const EqualWidthButton = styled.div`
  flex: 1 1 0;
  min-width: 0;

  ${mediaQueryUp.tablet} {
    flex: 0 0 auto;
  }
`

// Compact variant of LuxPlusMonthTakeoverJoiningFeeBanner for the LuxPlus+ landing page hero,
// with a floating label badge overlapping the border, matching this hero's narrower half-width
// column rather than the full-width campaign page.
function LuxPlusMonthTakeoverHeroJoiningFeeBanner() {
  const intl = useIntl()
  const { currentRegionCode } = useContext(GeoContext)
  const dispatch = useAppDispatch()
  const offer = useAppSelector(getBaseTierOfferWithJoiningFee)
  const isMobile = useScreenSizeOnly('mobile')
  const { handleBecomeMember } = useLuxPlusLandingActions()
  const regionSaveAmountCopy = getRegionSaveAmountCopy(currentRegionCode, intl)

  useEffect(() => {
    dispatch(fetchProductSubscription())
  }, [dispatch])

  if (!offer) {
    return null
  }

  const ctaLabel = intl.formatMessage({
    id: 'b5RoGuwYwrFpHZjXHFfC',
    defaultMessage: 'Join LuxPlus+',
    description: 'Call-to-action button label on the LuxPlus+ Month hero joining fee banner linking to the LuxPlus+ membership page',
  })

  return (
    <BannerContainer background="light-background" paletteType="default" borderRadius="L" padding="24 20">
      <LabelPosition>
        <NoWrapLabel kind="primary" variant="urgency">
          <FormattedMessage
            id="WVn8F3XkuYG1L4eiu_NH"
            defaultMessage="Limited-time offer"
            description="Label badge on the LuxPlus+ Month hero joining fee banner indicating the offer is only available for a limited time"
          />
        </NoWrapLabel>
      </LabelPosition>
      <Group direction="vertical" desktopDirection="horizontal" gap={16} desktopGap={8} horizontalAlign="center" desktopHorizontalAlign="start" verticalAlign="center">
        <Group direction="vertical" desktopDirection="horizontal" gap={2} desktopGap={12} horizontalAlign="center" desktopHorizontalAlign="start" verticalAlign="center">
          <Group direction="vertical" gap={2} horizontalAlign="center" desktopHorizontalAlign="start">
            <Heading variant="heading3">
              <RegularWeightText>
                <FormattedMessage
                  id="w5dlulZB90WLhUDilmoU"
                  defaultMessage="{joinFee} joining fee waived<footnote>^</footnote>"
                  description="Heading on the LuxPlus+ Month hero joining fee banner announcing the waived membership joining fee, with a footnote marker referencing the fine print below"
                  values={{
                    joinFee: <FormatCurrency value={offer.joinFee} format="roundedDollar" />,
                    footnote: (chunks) => <FootnoteMark>{chunks}</FootnoteMark>,
                  }}
                />
              </RegularWeightText>
            </Heading>
            <BodyText variant="medium" align="center" desktopAlign="start">
              <FormattedMessage
                id="uciaX3GJk9KVvF34RYOu"
                defaultMessage="Save {regionSaveAmountCopy} every year with LuxPlus+ member-only pricing."
                description="Subtitle on the LuxPlus+ Month hero joining fee banner explaining the value of LuxPlus+ member pricing"
                values={{ regionSaveAmountCopy }}
              />
            </BodyText>
          </Group>
        </Group>
        <Group direction="vertical" tabletDirection="horizontal" gap={16} tabletGap={16} desktopGap={16} verticalAlign="center" horizontalAlign="center" desktopHorizontalAlign="start">
          <Group direction="horizontal" desktopDirection="vertical" gap={4} desktopGap={0} verticalAlign="baseline" horizontalAlign="center" desktopHorizontalAlign="start">
            <BodyText variant="medium" weight="bold" colour="lux-plus" wrap="no-wrap">
              <FormattedMessage
                id="8rDiq_fpMGHkG_PCoaZp"
                defaultMessage="Join today for"
                description="Lead-in text before the discounted membership price on the LuxPlus+ Month hero joining fee banner"
              />
            </BodyText>
            <Group direction="horizontal" gap={4} verticalAlign="baseline">
              <Heading variant="heading4" colour="lux-plus">
                <FormatCurrency value={offer.price} format="roundedDollar" />
              </Heading>
              <PriceText variant="heading4" colour="lux-plus">
                <FormatCurrency value={offer.price + offer.joinFee} format="roundedDollar" />
              </PriceText>
            </Group>
          </Group>
          <Group direction="horizontal" gap={8} fullWidth={isMobile}>
            <ProductPaletteProvider palette="lux-plus">
              <EqualWidthButton>
                <TextButton kind="primary" size="medium" shape="round" fit="mobile-full-width" onClick={handleBecomeMember}>
                  {ctaLabel}
                </TextButton>
              </EqualWidthButton>
            </ProductPaletteProvider>
          </Group>
        </Group>
      </Group>
    </BannerContainer>
  )
}

export default LuxPlusMonthTakeoverHeroJoiningFeeBanner
